import { useEffect, useRef, useState } from "react";
import {
  getDropiIntegrations,
  getIntegrationsAvailability,
} from "../../../services/integrations/integrationsService";
import {
  getAllPaymentMethods,
  getPaymentMethodConfiguration,
} from "../../../services/payment/payment";

import {
  IntegrationType,
  INTEGRATIONS_UI_CONFIG,
} from "../config/integrations.config";
import { IntegrationAvailabilityApi } from "../config/integrations.types";
import { resolveAvailableIntegrations } from "../helpers/resolveAvailableIntegrations";
import { PaymentMethodStatus } from "../../payment/PaymentMethods/PaymentMethodsInterface";
import { DropiIntegrationInterface } from "../../../services/integrations/integrationsService";
import { Country } from "../../../const/countryList";
import { getWhatsAppAccount } from "../../../services/whatsapp/whatsappService";
import { decodeTokenService } from "../../../services/decodeTokenService";

export const useIntegrations = (country: Country = Country.COLOMBIA) => {
  const [integrationsDropi, setIntegrationsDropi] =
    useState<DropiIntegrationInterface>();
  const methodsStatus = useRef<PaymentMethodStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState<any[]>([]);

  const resolveIntegrationState = (
    type: IntegrationType,
    dropiData?: DropiIntegrationInterface
  ) => {
    if (type === "dropi") {
      const dropiIntegration = dropiData ?? integrationsDropi;
      const isActive = dropiIntegration?.is_active ?? false;
      return {
        isActive,
        isConnected: isActive,
        alreadyConfigured: isActive,
      };
    }

    const method = methodsStatus.current.find((m) => m.id === type);
    const active = method?.active ?? false;
    const alreadyConfigured = method?.already_configured ?? false;
    
    return {
      isActive: active && alreadyConfigured,
      isConnected: active && alreadyConfigured,
      alreadyConfigured,
    };
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);

      try {
        const ownerId = decodeTokenService.getUserIdFromToken();

        const results = await Promise.allSettled([
          getIntegrationsAvailability(),
          getDropiIntegrations(),
          getAllPaymentMethods(),
          getPaymentMethodConfiguration(),
          ownerId ? getWhatsAppAccount(ownerId) : Promise.reject("no owner"),
        ]);

        const availabilityApi = results[0].status === 'fulfilled' 
          ? results[0].value 
          : null;
        
        const dropiIntegration = results[1].status === 'fulfilled' 
          ? results[1].value 
          : undefined;
        
        const paymentConfig = results[3].status === 'fulfilled' 
          ? results[3].value 
          : [];

        results.forEach((result, index) => {
          if (result.status === 'rejected') {
            const endpointNames = [
              'getIntegrationsAvailability',
              'getDropiIntegrations',
              'getAllPaymentMethods',
              'getPaymentMethodConfiguration'
            ] as const;
            const error = result.reason;
            const is404 = error?.response?.status === 404;
            
            if (is404) {
              console.warn(`${endpointNames[index]} returned 404 (expected for some users)`);
            } else {
              console.error(`Error in ${endpointNames[index]}:`, error);
            }
          }
        });

        if (dropiIntegration) {
          setIntegrationsDropi(dropiIntegration);
        }
        
        methodsStatus.current = paymentConfig;

        if (availabilityApi) {
          const available = resolveAvailableIntegrations({
            apiIntegrations: availabilityApi as IntegrationAvailabilityApi[],
            country,
          });

          const finalIntegrations = available.map((integration) => {
            const ui = INTEGRATIONS_UI_CONFIG[integration.id];
            const state = resolveIntegrationState(integration.id, dropiIntegration);

            return {
              ...ui,
              ...state,
              type: integration.id,
            };
          });

          // Append WhatsApp card (managed by separate service, not ecommerce-service)
          const whatsappResult = results[4];
          const whatsappAccount = whatsappResult.status === 'fulfilled' ? whatsappResult.value : null;
          const whatsappIsActive = whatsappAccount?.is_active ?? false;
          const whatsappCard = {
            ...INTEGRATIONS_UI_CONFIG.whatsapp,
            isActive: whatsappIsActive,
            isConnected: whatsappIsActive,
            alreadyConfigured: !!whatsappAccount,
            type: 'whatsapp' as IntegrationType,
          };


          import.meta.env.VITE_DISABLED_CHAT === 'false' ? setIntegrations([...finalIntegrations, whatsappCard]) : setIntegrations([...finalIntegrations]);
        } else {
          // Even if other integrations fail, still show WhatsApp card
          const whatsappResult = results[4];
          const whatsappAccount = whatsappResult.status === 'fulfilled' ? whatsappResult.value : null;
          const whatsappIsActive = whatsappAccount?.is_active ?? false;
          const whatsappCard = {
            ...INTEGRATIONS_UI_CONFIG.whatsapp,
            isActive: whatsappIsActive,
            isConnected: whatsappIsActive,
            alreadyConfigured: !!whatsappAccount,
            type: 'whatsapp' as IntegrationType,
          };

          import.meta.env.VITE_DISABLED_CHAT === 'false' ? setIntegrations([whatsappCard]) : setIntegrations([]);
        }
      } catch (error) {
        console.error("Unexpected error loading integrations:", error);
        setIntegrations([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [country]);

  return {
    integrations,
    loading,
  };
};
